/**
 * External dependencies
 */
import { Button, DatePicker, Popover, SelectControl, TextControl } from '@wordpress/components';
import { __, sprintf } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import moment from 'moment';

/**
 * Internal dependencies
 */
import './style.scss';

const AVAILABILITY_ALL_DAY        = '1';
const AVAILABILITY_SPECIFIC_HOURS = '2';

const DOES_NOT_REPEAT        = '1';
const REPEATS_WEEKLY         = '2';
const REPEATS_YEARLY         = '3';
const REPEATS_SPECIFIC_DAYS  = '4';
const REPEATS_SPECIFIC_DATES = '5';

function StoreAvailabilityPopover( props ) {
	const [ isPopoverVisible, setPopoverVisible ]               = useState( true );
	const [ startDate, setStartDate ]                           = useState( props.selectedDate || new Date() );
	const [ needsEndDate, setNeedsEndDate ]                     = useState( false );
	const [ endDate, setEndDate ]                               = useState( props.selectedDate || new Date() );
	const [ isDatePickerVisible, setDatePickerVisible ]         = useState( false );
	const [ availabilityTime, setAvailabilityTime ]             = useState( AVAILABILITY_ALL_DAY );
	const [ availabilityTimeFrames, setAvailabilityTimeFrames ] = useState( [ ] );
	const [ frequency, setFrequency ]                           = useState( DOES_NOT_REPEAT );
	const [ repeatDaysOfWeek, setRepeatDaysOfWeek ]             = useState();

	const selectedDayOfWeek = moment( props.selectedDate ).format( 'dddd' );
	const selectedDay       = moment( props.selectedDate ).format( 'MMMM D' );

	function popoverContent() {
		return ( <div className="wb__sap">
			<h4 className="wb__sap__heading">{ __( 'Edit Availability', 'woocommerce-bookings' ) }</h4>
			<div className="wb__sap__start-end-dates wb__sap__row">
				<TextControl className="wb__sap__start-date" label={ __( 'Start Date', 'woocommerce-bookings' ) } onClick={ selectStartDate } value={ formatDate( startDate ) }/>
				{ !needsEndDate ? (
					<span className="wb__sap__add-end-date" onClick={ selectEndDate }>{ __( 'Add end date', 'woocommerce-bookings' ) }</span>
				) : (
					<TextControl className="wb__sap__end-date" label={ __( 'End Date', 'woocommerce-bookings' ) } onClick={ selectEndDate } value={ formatDate( endDate ) }/>
				)}
			</div>
			<div className="wb__sap__time wb__sap__row">
				<SelectControl label={ __( 'Time', 'woocommerce-bookings' ) } className="wb__sap__time-selector" value={ availabilityTime } onChange={ changeAvailabilityTime } options={
					[
						{ label: __( 'Available all day', 'woocommerce-bookings' ), value: AVAILABILITY_ALL_DAY },
						{ label: __( 'Available at specific hours', 'woocommerce-bookings' ), value: AVAILABILITY_SPECIFIC_HOURS }
					]
				} />
				{ isAvailableAtSpecificHours() && renderTimeFrames( availabilityTimeFrames, setAvailabilityTimeFrames ) }
			</div>
			<div className="wb__sap__frequency wb__sap__row">
				<SelectControl label={ __( 'Frequency', 'woocommerce-bookings' ) } className="wb__sap__frequency-selector" value={ frequency } onChange={ changeFrequency } options={
					[
						{ label:  __( 'Does not repeat', 'woocommerce-bookings' ) , value: DOES_NOT_REPEAT },
						/* translators: %s: day of week */
						{ label:  sprintf( __( 'Every %s', 'woocommerce-bookings' ), selectedDayOfWeek ) , value: REPEATS_WEEKLY },
						/* translators: %s: day of year */
						{ label:  sprintf(  __( 'Anually on %s', 'woocommerce-bookings' ), selectedDay ) , value: REPEATS_YEARLY },
						{ label:  __( 'On specific days of the week', 'woocommerce-bookings' ) , value: REPEATS_SPECIFIC_DAYS },
						{ label:  __( 'On specific dates', 'woocommerce-bookings' ) , value: REPEATS_SPECIFIC_DATES }
					]
				} />
				{ doesRepeatSpecificDaysOfWeek() &&
					<div className="wb__sap__frequency-days-of-week wb__sap__row">
						<span>{ __( 'Repeats on:', 'woocommerce-bookings' ) }</span>
						<ul className="wb__sap__days-of-week-selector">
							{ daysOfWeekSelector( changeRepeatDayOfWeek, repeatDaysOfWeek ) }
						</ul>
					</div>
				}
			</div>
			<div className="wb__sap__actions wb__sap__row">
				<Button className="wb__sap__cancel" isTertiary isLink onClick={ closePopover }>{ __( 'Cancel', 'woocommerce-bookings' ) }</Button>
				<Button className="wb__sap__apply" isDefault isPrimary>{ __( 'Apply', 'woocommerce-bookings' ) }</Button>
			</div>
		</div> );
	}

	function datePicker() {
		return (
			<Popover className="wb__sap_date-picker" position="middle center" onClickOutside={ () => setDatePickerVisible( false ) } focusOnMount={ false }>
				<DatePicker
					currentDate={ startDate }
					onChange={ ( date ) => {
						setDatePickerVisible( false );
						updateDate( date )
					} }
				/>
			</Popover>
		);
	}

	function selectStartDate() {
		setDatePickerVisible( !isDatePickerVisible )
		updateDate = setStartDate;
	}

	function selectEndDate() {
		setNeedsEndDate( true );
		setDatePickerVisible( !isDatePickerVisible )
		updateDate = setEndDate;
	}

	function changeAvailabilityTime( newAvailabilityTime ) {
		setAvailabilityTime( newAvailabilityTime );
		if ( availabilityTimeFrames.length === 0 ) {
			availabilityTimeFrames.push( [ '0', '0' ] );
			setAvailabilityTimeFrames( availabilityTimeFrames );
		}
	}

	function isAvailableAtSpecificHours() {
		return availabilityTime === AVAILABILITY_SPECIFIC_HOURS;
	}

	function changeFrequency( newFrequency ) {
		setFrequency( newFrequency );
	}

	function doesRepeatSpecificDaysOfWeek() {
		return frequency === REPEATS_SPECIFIC_DAYS;
	}

	function changeRepeatDayOfWeek( event ) {
		const dayOfWeek = parseInt(event.target.value);
		let selectedDays = repeatDaysOfWeek || [];
		if ( !selectedDays.includes( dayOfWeek ) ) {
			selectedDays.push( dayOfWeek );
		} else {
			selectedDays = selectedDays.filter( day => day !== dayOfWeek );
		}

		setRepeatDaysOfWeek( selectedDays.slice() );
	}

	function closePopover() {
		if ( ! isDatePickerVisible ) {
			setPopoverVisible( false );
		}
	}

	return (
		isPopoverVisible && <div className="wb__sap__container">
			<Popover position="middle center" onClickOutside={ closePopover } focusOnMount={ false }>
				{ popoverContent() }
			</Popover>
			{ isDatePickerVisible && datePicker() }
		</div>
	);
}

function formatDate( date ) {
	return moment( date ).format( 'MMM DD, YYYY' );
}

function renderTimeFrames( availabilityTimeFrames, setAvailabilityTimeFrames ) {
	const timeFrames = []
	function changeTimeFrame( timeFrameIndex, comboIndex ) {
		return function( newTime ) {
			availabilityTimeFrames[ timeFrameIndex ][ comboIndex ] = newTime;

			setAvailabilityTimeFrames( availabilityTimeFrames.slice() );
		}
	}

	function addAnotherTimeFrame() {
		availabilityTimeFrames.push( [ '0', '0' ] );
		setAvailabilityTimeFrames( availabilityTimeFrames.slice() );
	}

	for (var timeFrameIndex = 0; timeFrameIndex < availabilityTimeFrames.length; timeFrameIndex++) {
		const timeFrame = (
			<div key={ timeFrameIndex } className="wb__sap__time-frame wb__sap__row">
				{ hoursOfDayCombo( availabilityTimeFrames[timeFrameIndex][0], changeTimeFrame(timeFrameIndex, 0), timeFrameIndex, 0) }
				<span>—</span>
				{ hoursOfDayCombo( availabilityTimeFrames[timeFrameIndex][1], changeTimeFrame(timeFrameIndex, 1), timeFrameIndex, 1) }
			</div> );
		timeFrames.push( timeFrame );
	}
	timeFrames.push( <div key="add-more-times" className="wb__sap__add-new-time-frame" onClick={ addAnotherTimeFrame }>{ __( 'Add more times', 'woocommerce-bookings' ) }</div> );

	return timeFrames;
}

function hoursOfDayCombo( selectedTime, updateSelectedTime, timeFrameIndex, selectIndex ) {
	const options = [];
	for (var hour = 0; hour < 24; hour++) {
		options.push(
			{ label:  moment().startOf( 'day' ).add( hour, 'hour' ).format( 'h:mma' ) , value: hour }
		);
	}

	return (
		<SelectControl key={ `${timeFrameIndex}-${selectIndex}` } value={ selectedTime } onChange={ updateSelectedTime } className="wb__sap__time-frame-hours-selector" options={ options } />
	);
}

function daysOfWeekSelector( changeRepeatDayOfWeek, repeatDaysOfWeek ) {
	const daysOfWeek = [];
	for ( var dayIndex = 0; dayIndex < 7; dayIndex ++ ) {
		const day = moment().startOf( 'week' ).add( dayIndex, 'days' )
		const dayOfWeek = day.weekday();
		const dayName = day.format( 'dd' );
		const selected = ( repeatDaysOfWeek || [] ).includes( dayOfWeek );
		const classNames = [ 'wb__sap__day-of-week-button' ]
		if ( selected ) { classNames.push( 'selected' ); }

		daysOfWeek.push(
			<li key={ dayIndex }>
				<button className={ classNames.join( ' ' ) } onClick={ changeRepeatDayOfWeek } value={ dayOfWeek } data-day={ dayName }>{ dayName }</button>
			</li>
		);
	}

	return daysOfWeek;
}

export default StoreAvailabilityPopover;
